import type { StateAction } from "@entrilia/shared/types";
import { formatDateShort, formatDateTimeShort } from "@entrilia/shared/utilities/dateUtils";
import type { InvestorPortalWatermarkSettings, WatermarkSegment } from "../../../../api/types/investorPortalTypes";

const previewDate = new Date(2025, 0, 24, 10, 25);

const getDatePreviewValue = (includeTime: boolean): string =>
  includeTime ? formatDateTimeShort(previewDate) : formatDateShort(previewDate);

export interface WatermarkSettingsState {
  isDirty: boolean;
  watermarkEnabled: boolean;
  includeTime: boolean;
  categoriesOption: WatermarkCategoriesOption;
  externalCategoryIds: string[];
  items: {
    customText: WatermarkItem;
    contactName: WatermarkItem;
    contactEmail: WatermarkItem;
    investorName: WatermarkItem;
    dateAndTime: WatermarkItem;
  };
}

export interface WatermarkItem {
  id: WatermarkSegment;
  value: string;
  isVisible: boolean;
}

export type WatermarkCategoriesOption = "all_categories" | "selected_categories";

export type WatermarkItemKey = keyof WatermarkSettingsState["items"];

export interface WatermarkSettingsStateValidationResult {
  isValid: boolean;
  customTextError?: string;
  enabledItemsError?: string;
  documentCategoriesError?: string;
}

export const getWatermarkSettingsStateFromSavedSettings = (
  settings: InvestorPortalWatermarkSettings
): WatermarkSettingsState => {
  const includeTime = !settings.showDateWithoutTime && settings.segments.includes("DateAndTime");

  return {
    isDirty: false,
    watermarkEnabled: settings.isEnabled,
    includeTime,
    categoriesOption: Array.isArray(settings.externalCategoryIds) ? "selected_categories" : "all_categories",
    externalCategoryIds: settings.externalCategoryIds ?? [],
    items: {
      customText: {
        id: "CustomText",
        value: settings.customText ?? "Confidential",
        isVisible: settings.segments.includes("CustomText"),
      },
      contactName: {
        id: "ContactName",
        value: "John Doe",
        isVisible: settings.segments.includes("ContactName"),
      },
      contactEmail: {
        id: "ContactEmail",
        value: "john.doe@example.com",
        isVisible: settings.segments.includes("ContactEmail"),
      },
      investorName: {
        id: "InvestorName",
        value: "Investor ABC",
        isVisible: settings.segments.includes("InvestorName"),
      },
      dateAndTime: {
        id: "DateAndTime",
        value: getDatePreviewValue(includeTime),
        isVisible: settings.segments.includes("DateAndTime"),
      },
    },
  };
};

export const mapWatermarkSettingsStateToUpdateRequest = (
  state: WatermarkSettingsState
): InvestorPortalWatermarkSettings => {
  const selectedSegments = Object.values(state.items)
    .filter((item) => item.isVisible)
    .map((item) => item.id);

  return {
    isEnabled: state.watermarkEnabled,
    segments: selectedSegments,
    customText: state.items.customText.value,
    externalCategoryIds: state.categoriesOption === "selected_categories" ? state.externalCategoryIds : undefined,
    showDateWithoutTime: !state.includeTime,
  };
};

export const validateWatermarkSettingsState = (
  settingsState: WatermarkSettingsState
): WatermarkSettingsStateValidationResult => {
  const customTextError =
    settingsState.watermarkEnabled &&
    settingsState.items.customText.isVisible &&
    !settingsState.items.customText.value.trim()
      ? "This text is required"
      : undefined;

  const enabledItemsError =
    settingsState.watermarkEnabled && Object.values(settingsState.items).every((item) => !item.isVisible)
      ? "At least one segment must be selected"
      : undefined;

  const documentCategoriesError =
    settingsState.watermarkEnabled &&
    settingsState.categoriesOption === "selected_categories" &&
    settingsState.externalCategoryIds.length === 0
      ? "At least one category must be selected"
      : undefined;

  const isValid = !customTextError && !enabledItemsError && !documentCategoriesError;

  return {
    isValid,
    customTextError,
    enabledItemsError,
    documentCategoriesError,
  };
};

// Actions

export const setWatermarkEnabledAction =
  (watermarkEnabled: boolean): StateAction<WatermarkSettingsState> =>
  (state) => ({
    ...state,
    isDirty: true,
    watermarkEnabled,
  });

export const setCategoriesOptionAction =
  (categoriesOption: WatermarkCategoriesOption): StateAction<WatermarkSettingsState> =>
  (state) => ({
    ...state,
    isDirty: true,
    categoriesOption,
  });

export const setExternalCategoryIdsAction =
  (externalCategoryIds: string[]): StateAction<WatermarkSettingsState> =>
  (state) => ({
    ...state,
    isDirty: true,
    externalCategoryIds,
  });

export const setItemVisibilityAction =
  (itemKey: WatermarkItemKey, isVisible: boolean): StateAction<WatermarkSettingsState> =>
  (state) => ({
    ...state,
    isDirty: true,
    items: {
      ...state.items,
      [itemKey]: { ...state.items[itemKey], isVisible },
    },
  });

export const setCustomTextAction =
  (value: string): StateAction<WatermarkSettingsState> =>
  (state) => ({
    ...state,
    isDirty: true,
    items: {
      ...state.items,
      customText: { ...state.items.customText, value },
    },
  });

export const setIncludeTimeAction =
  (includeTime: boolean): StateAction<WatermarkSettingsState> =>
  (state) => ({
    ...state,
    isDirty: true,
    includeTime,
    items: {
      ...state.items,
      dateAndTime: {
        ...state.items.dateAndTime,
        value: getDatePreviewValue(includeTime),
      },
    },
  });
